import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import Moment from "react-moment";
import "moment/locale/pt-br";
import "./style.css";
import axios from "axios";
import { FeedPostMenu } from "../shared";
import EditPostPopup from "../../editPostPopup";
import VotingNotifications from "./VotingNotifications";
import VotingCreatorTools from "./VotingCreatorTools";

const initialCreateData = {
  title: "",
  description: "",
  category: "general",
  options: ["", ""],
  startDate: "",
  endDate: "",
  isAnonymous: false,
  allowMultiple: false,
  allowComments: true,
  resultsVisible: true,
  // Advanced features
  votingType: "public", // "public", "restricted", "closed"
  regionScope: "worldwide", // "worldwide", "national", "regional", etc
  allowedUsers: [], // for closed voting
  requiresDocumentation: false,
  documentationRequirements: [],
};

const regionScopes = [
  { id: "worldwide", name: "🌍 Mundialmente" },
  { id: "international", name: "🌐 Internacional" },
  { id: "continental", name: "🗺️ Continental" },
  { id: "regional", name: "📍 Regional" },
  { id: "national", name: "🇧🇷 Nacional" },
  { id: "provincial", name: "📌 Provincial/Estadual" },
  { id: "municipal", name: "🏙️ Municipal/Cidade" },
  { id: "local", name: "🏘️ Local/Bairro" },
  { id: "institutional", name: "🏢 Institucional/Empresa" },
];

const votingTypes = [
  { id: "public", name: "🔓 Pública - Qualquer usuário pode votar" },
  { id: "restricted", name: "🔐 Restrita - Requer registro/aprovação" },
  { id: "closed", name: "🔒 Fechada - Apenas usuários selecionados" },
];

export default function VotingFeed() {
  const { user } = useSelector((state) => ({ ...state }));
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [votedPolls, setVotedPolls] = useState({});
  const [editingPost, setEditingPost] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creatingVote, setCreatingVote] = useState(false);
  const [createData, setCreateData] = useState(initialCreateData);
  const [category, setCategory] = useState("general");
  const [view, setView] = useState("feed"); // "feed", "trending", "myVotes", "created", "history", "viewed"
  const [subscriptions, setSubscriptions] = useState({}); // Track which votings user is subscribed to
  const [stats, setStats] = useState({ created: 0, voted: 0, viewed: 0 });
  const [historySort, setHistorySort] = useState("recent"); // "recent", "popular", "oldest"

  const categories = [
    { id: "general", name: "Geral" },
    { id: "sports", name: "Esportes" },
    { id: "entertainment", name: "Entretenimento" },
    { id: "technology", name: "Tecnologia" },
    { id: "politics", name: "Política" },
    { id: "opinion", name: "Opinião" },
  ];
  const filters = [
    { id: "all", name: "Todas" },
    { id: "active", name: "Ativas" },
    { id: "ended", name: "Encerradas" },
    { id: "popular", name: "Populares" },
  ];

  const fetchPosts = async (pageNum = 1) => {
    try {
      let url;
      if (category && category !== "general") {
        url = `${process.env.REACT_APP_BACKEND_URL}/voting/category/${category}?page=${pageNum}&limit=12`;
      } else {
        url = `${process.env.REACT_APP_BACKEND_URL}/feed/voting?page=${pageNum}&limit=12`;
        if (filter !== "all") url += `&filter=${filter}`;
      }

      const { data } = await axios.get(url, { headers: { Authorization: `Bearer ${user.token}` } });
      if (pageNum === 1) setPosts(data.posts || data || []);
      else setPosts((prev) => [...prev, ...(data.posts || data || [])]);

      if (data.pagination) {
        setHasMore((data.pagination?.page || 1) < (data.pagination?.pages || 1));
      } else {
        setHasMore(false);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching voting:", error);
      setLoading(false);
    }
  };

  const fetchByView = async (pageNum = 1) => {
    try {
      let url;

      switch (view) {
        case "trending":
          url = `${process.env.REACT_APP_BACKEND_URL}/voting/trending/all?limit=20`;
          break;
        case "myVotes":
          url = `${process.env.REACT_APP_BACKEND_URL}/voting/user/${user._id}/votes?page=${pageNum}&limit=12`;
          break;
        case "created":
          url = `${process.env.REACT_APP_BACKEND_URL}/voting/user/created/all?page=${pageNum}&limit=12`;
          break;
        case "history":
          url = `${process.env.REACT_APP_BACKEND_URL}/voting/history/all?page=${pageNum}&limit=20&sortBy=${historySort}`;
          break;
        case "viewed":
          url = `${process.env.REACT_APP_BACKEND_URL}/voting/history/viewed?page=${pageNum}&limit=12`;
          break;
        case "feed":
          return fetchPosts(pageNum);
        default:
          return fetchPosts(pageNum);
      }

      const { data } = await axios.get(url, { headers: { Authorization: `Bearer ${user.token}` } });
      if (pageNum === 1) setPosts(data.posts || data.viewedVotings?.map(v => v.voting) || data || []);
      else setPosts((prev) => [...prev, ...(data.posts || data.viewedVotings?.map(v => v.voting) || data || [])]);

      if (data.pagination) {
        setHasMore(data.pagination.page < data.pagination.pages);
      } else {
        setHasMore(false);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching by view:", error);
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/voting/stats`,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      setStats(data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setPage(1);
    setLoading(true);
    if (view === "feed") {
      fetchPosts(1);
    } else {
      fetchByView(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, view, category]);
  const handleEditPost = (post) => {
    setEditingPost(post);
  };

  const handlePostUpdated = (updatedPost) => {
    setPosts((prev) => prev.map((p) => (p._id === updatedPost._id ? updatedPost : p)));
    setEditingPost(null);
  };

  const handlePostDeleted = (postId) => {
    setPosts((prev) => prev.filter((p) => p._id !== postId));
  };

  const handleVote = async (postId, optionIndex) => {
    const post = posts.find((p) => p._id === postId);
    if (!post || !post.votingData) return;

    const allowMultiple = !!post.votingData.allowMultiple;
    const existingEntry = votedPolls[postId];
    const alreadyVoted = Array.isArray(existingEntry)
      ? existingEntry.includes(optionIndex)
      : existingEntry !== undefined || post.votingData.hasVoted;

    if (!allowMultiple && alreadyVoted) return;

    setVotedPolls((prev) => {
      if (allowMultiple) {
        const current = Array.isArray(prev[postId]) ? prev[postId] : [];
        if (current.includes(optionIndex)) return prev;
        return { ...prev, [postId]: [...current, optionIndex] };
      }
      return { ...prev, [postId]: optionIndex };
    });

    setPosts((prevPosts) => prevPosts.map((p) => {
      if (p._id === postId) {
        const options = [...(p.votingData?.options || [])];
        if (options[optionIndex]) {
          options[optionIndex] = {
            ...options[optionIndex],
            votes: (options[optionIndex].votes || 0) + 1,
          };
        }
        return {
          ...p,
          votingData: {
            ...p.votingData,
            options,
            totalVotes: (p.votingData?.totalVotes || 0) + 1,
          },
        };
      }
      return p;
    }));

    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/voting/${postId}/vote`,
        { optionIndex },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
    } catch (error) {
      console.error("Error voting:", error);
    }
  };

  const getPercentage = (votes, total) => {
    if (!total) return 0;
    return Math.round((votes / total) * 100);
  };

  const handleCreateVote = async (e) => {
    e.preventDefault();
    const trimmedTitle = createData.title.trim();
    if (trimmedTitle.length < 5) {
      alert("A pergunta deve ter no mínimo 5 caracteres");
      return;
    }

    if (createData.options.filter((o) => o.trim()).length < 2) {
      alert("Preenchimento obrigatório: Título e pelo menos 2 opções");
      return;
    }

    setCreatingVote(true);
    try {
      const votingPayload = {
        title: trimmedTitle,
        description: createData.description,
        category: createData.category || "general",
        options: createData.options
          .filter((o) => o.trim())
          .map((text) => ({ text, votes: 0, voters: [] })),
        startDate: createData.startDate || null,
        endDate: createData.endDate || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
        isAnonymous: createData.isAnonymous,
        allowMultiple: createData.allowMultiple,
        isActive: true,
        totalVotes: 0,
        allowComments: createData.allowComments !== false,
        resultsVisible: createData.resultsVisible !== false,
        isPaid: !!createData.isPaid,
        entryFee: createData.isPaid ? Number(createData.entryFee || 0) : 0,
        feeCurrency: "BRL",
        // Advanced voting features
        votingType: createData.votingType || "public",
        regionScope: createData.regionScope || "worldwide",
        allowedUsers: createData.allowedUsers || [],
        requiresDocumentation: createData.requiresDocumentation || false,
        documentationRequirements: createData.documentationRequirements || [],
      };

      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/editorial/create`,
        {
          editorialType: "voting",
          votingData: votingPayload,
          text: trimmedTitle,
        },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );

      setPosts((prev) => [data, ...prev]);
      setShowCreateModal(false);
      setCreateData(initialCreateData);
    } catch (error) {
      console.error("Erro ao criar votação:", error);
      alert("Erro ao criar votação");
    } finally {
      setCreatingVote(false);
    }
  };

  const handleOptionChange = (index, value) => {
    const newOptions = [...createData.options];
    newOptions[index] = value;
    setCreateData({ ...createData, options: newOptions });
  };

  const addOption = () => {
    setCreateData({
      ...createData,
      options: [...createData.options, ""],
    });
  };

  const removeOption = (index) => {
    if (createData.options.length > 2) {
      const newOptions = createData.options.filter((_, i) => i !== index);
      setCreateData({ ...createData, options: newOptions });
    }
  };

  const isExpired = (endDate) => {
    if (!endDate) return false;
    return new Date(endDate) < new Date();
  };

  const getVotingStatus = (startDate, endDate) => {
    if (isExpired(endDate)) {
      return { status: "closed", label: "Encerrada", color: "#f44336" };
    }
    if (startDate && new Date(startDate) > new Date()) {
      return { status: "scheduled", label: "Agendada", color: "#2196F3" };
    }
    return { status: "active", label: "Ativa", color: "#4CAF50" };
  };

  const handleShare = (postId, title) => {
    const shareUrl = `${window.location.origin}/voting?poll=${postId}`;
    const shareText = `Voto em: ${title}`;

    if (navigator.share) {
      navigator.share({
        title: "SocializaBD",
        text: shareText,
        url: shareUrl,
      }).catch((err) => console.log("Erro ao compartilhar:", err));
    } else {
      navigator.clipboard.writeText(shareUrl);
      alert("Link copiado para clipboard!");
    }
  };

  const handleSubscriptionChange = (postId, isSubscribed) => {
    setSubscriptions((prev) => ({ ...prev, [postId]: isSubscribed }));
  };

  const handleCopyResults = (postId) => {
    const post = posts.find((p) => p._id === postId);
    if (!post || !post.votingData) return;

    let text = `📊 ${post.votingData.title || post.text}\n\n`;
    post.votingData.options.forEach((opt) => {
      const pct = Math.round((opt.votes / (post.votingData.totalVotes || 1)) * 100);
      text += `${opt.text}: ${opt.votes} votos (${pct}%)\n`;
    });
    text += `\nTotal: ${post.votingData.totalVotes} votos`;

    navigator.clipboard.writeText(text);
    alert("Resultados copiados!");
  };

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    if (view === "feed") {
      fetchPosts(nextPage);
    } else {
      fetchByView(nextPage);
    }
  };

  return (
    <div className="voting_feed">
      <button
        className="voting_create_btn"
        onClick={() => setShowCreateModal(true)}
      >
        ✍️ Criar Votação
      </button>

      {/* Estatísticas */}
      <div className="voting_stats">
        <div className="stat_item">
          <span className="stat_number">{stats.created}</span>
          <span className="stat_label">Criadas</span>
        </div>
        <div className="stat_item">
          <span className="stat_number">{stats.voted}</span>
          <span className="stat_label">Votadas</span>
        </div>
        <div className="stat_item">
          <span className="stat_number">{stats.viewed}</span>
          <span className="stat_label">Visualizadas</span>
        </div>
      </div>

      <div className="voting_tabs">
        <button
          className={`voting_tab ${view === "feed" ? "active" : ""}`}
          onClick={() => setView("feed")}
        >
          Feed
        </button>
        <button
          className={`voting_tab ${view === "trending" ? "active" : ""}`}
          onClick={() => setView("trending")}
        >
          🔥 Trending
        </button>
        <button
          className={`voting_tab ${view === "myVotes" ? "active" : ""}`}
          onClick={() => setView("myVotes")}
        >
          Minhas Votações
        </button>
        <button
          className={`voting_tab ${view === "created" ? "active" : ""}`}
          onClick={() => setView("created")}
        >
          Criadas
        </button>
        <button
          className={`voting_tab ${view === "history" ? "active" : ""}`}
          onClick={() => setView("history")}
        >
          📜 Histórico Completo
        </button>
        <button
          className={`voting_tab ${view === "viewed" ? "active" : ""}`}
          onClick={() => setView("viewed")}
        >
          👁️ Visualizadas
        </button>
      </div>

      {/* Controle de ordenação para histórico */}
      {view === "history" && (
        <div className="voting_history_controls">
          <select
            value={historySort}
            onChange={(e) => {
              setHistorySort(e.target.value);
              setPage(1);
              fetchByView(1);
            }}
            className="history_sort_select"
          >
            <option value="recent">Mais Recentes</option>
            <option value="popular">Mais Populares</option>
            <option value="oldest">Mais Antigas</option>
          </select>
        </div>
      )}

      {view === "feed" && (
        <div className="voting_category_selector">
          {categories.map((cat) => (
            <button
              key={cat.id}
              className={`category_chip ${category === cat.id ? "active" : ""}`}
              onClick={() => setCategory(cat.id)}
            >
              {cat.name}
            </button>
          ))}
        </div>
      )}

      {showCreateModal && (
        <div className="voting_create_modal_overlay" onClick={() => setShowCreateModal(false)}>
          <div className="voting_create_modal" onClick={(e) => e.stopPropagation()}>
            <div className="voting_modal_header">
              <h2>Criar Votação</h2>
              <button
                className="voting_modal_close"
                onClick={() => setShowCreateModal(false)}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreateVote} className="voting_create_form">
              <div className="voting_form_group">
                <label>Pergunta *</label>
                <input
                  type="text"
                  placeholder="O que você quer perguntar?"
                  value={createData.title}
                  onChange={(e) => setCreateData({ ...createData, title: e.target.value })}
                  maxLength="200"
                />
              </div>

              <div className="voting_form_group">
                <label>Descrição (opcional)</label>
                <textarea
                  placeholder="Adicione detalhes sobre a votação..."
                  value={createData.description}
                  onChange={(e) => setCreateData({ ...createData, description: e.target.value })}
                  maxLength="500"
                  rows="3"
                />
              </div>

              <div className="voting_form_group">
                <label>Categoria</label>
                <select
                  value={createData.category || "general"}
                  onChange={(e) => setCreateData({ ...createData, category: e.target.value })}
                >
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <div className="voting_form_group">
                <label>Opções *</label>
                <div className="voting_options_input">
                  {createData.options.map((option, index) => (
                    <div key={index} className="voting_option_input">
                      <input
                        type="text"
                        placeholder={`Opção ${index + 1}`}
                        value={option}
                        onChange={(e) => handleOptionChange(index, e.target.value)}
                        maxLength="100"
                      />
                      {createData.options.length > 2 && (
                        <button
                          type="button"
                          className="voting_remove_option"
                          onClick={() => removeOption(index)}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    className="voting_add_option"
                    onClick={addOption}
                  >
                    + Adicionar Opção
                  </button>
                </div>
              </div>

              <div className="voting_form_group">
                <label>Início</label>
                <input
                  type="datetime-local"
                  value={createData.startDate}
                  onChange={(e) => setCreateData({ ...createData, startDate: e.target.value })}
                />
              </div>

              <div className="voting_form_group">
                <label>Encerramento</label>
                <input
                  type="datetime-local"
                  value={createData.endDate}
                  onChange={(e) => setCreateData({ ...createData, endDate: e.target.value })}
                />
              </div>

              <div className="voting_form_checks">
                <label className="voting_checkbox">
                  <input
                    type="checkbox"
                    checked={createData.isAnonymous}
                    onChange={(e) => setCreateData({ ...createData, isAnonymous: e.target.checked })}
                  />
                  <span>Votação anônima</span>
                </label>
                <label className="voting_checkbox">
                  <input
                    type="checkbox"
                    checked={createData.allowMultiple}
                    onChange={(e) => setCreateData({ ...createData, allowMultiple: e.target.checked })}
                  />
                  <span>Permitir múltiplas escolhas</span>
                </label>
                <label className="voting_checkbox">
                  <input
                    type="checkbox"
                    checked={createData.allowComments}
                    onChange={(e) => setCreateData({ ...createData, allowComments: e.target.checked })}
                  />
                  <span>Permitir comentários</span>
                </label>
                <label className="voting_checkbox">
                  <input
                    type="checkbox"
                    checked={createData.resultsVisible}
                    onChange={(e) => setCreateData({ ...createData, resultsVisible: e.target.checked })}
                  />
                  <span>Mostrar resultados antes do fim</span>
                </label>
                <label className="voting_checkbox">
                  <input
                    type="checkbox"
                    checked={createData.isPaid || false}
                    onChange={(e) => setCreateData({ ...createData, isPaid: e.target.checked })}
                  />
                  <span>Votação paga (taxa por voto)</span>
                </label>
              </div>

              {createData.isPaid && (
                <div className="voting_form_group">
                  <label>Taxa por voto (BRL)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={createData.entryFee || 0}
                    onChange={(e) => setCreateData({ ...createData, entryFee: e.target.value })}
                  />
                </div>
              )}

              <div className="voting_form_divider">
                <h4>⚙️ Configurações Avançadas</h4>
              </div>

              <div className="voting_form_group">
                <label>Tipo de Votação</label>
                <select
                  value={createData.votingType || "public"}
                  onChange={(e) => setCreateData({ ...createData, votingType: e.target.value })}
                >
                  {votingTypes.map((type) => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </select>
              </div>

              <div className="voting_form_group">
                <label>Escopo Geográfico</label>
                <select
                  value={createData.regionScope || "worldwide"}
                  onChange={(e) => setCreateData({ ...createData, regionScope: e.target.value })}
                >
                  {regionScopes.map((scope) => (
                    <option key={scope.id} value={scope.id}>{scope.name}</option>
                  ))}
                </select>
              </div>

              {createData.votingType === "restricted" && (
                <div className="voting_form_group">
                  <label className="voting_checkbox">
                    <input
                      type="checkbox"
                      checked={createData.requiresDocumentation}
                      onChange={(e) => setCreateData({ ...createData, requiresDocumentation: e.target.checked })}
                    />
                    <span>Exigir documentação para votação</span>
                  </label>
                  {createData.requiresDocumentation && (
                    <div className="voting_docs_input">
                      <label>Documentos solicitados (separados por vírgula)</label>
                      <input
                        type="text"
                        placeholder="Ex: Cédula de Identidade, Comprovante de Residência"
                        value={createData.documentationRequirements.join(", ")}
                        onChange={(e) => 
                          setCreateData({ 
                            ...createData, 
                            documentationRequirements: e.target.value.split(",").map(d => d.trim()).filter(d => d)
                          })
                        }
                      />
                    </div>
                  )}
                </div>
              )}

              <button
                type="submit"
                className="voting_submit_btn"
                disabled={creatingVote}
              >
                {creatingVote ? "Criando..." : "Criar Votação"}
              </button>
            </form>
          </div>
        </div>
      )}

      {filters.length > 0 && (
        <div className="simple_filter_bar">
          <div className="filter_buttons">
            {filters.map((filterItem) => (
              <button
                key={filterItem.id}
                className={`filter_btn ${filter === filterItem.id ? "active" : ""}`}
                onClick={() => setFilter(filterItem.id)}
              >
                {filterItem.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {posts.length === 0 ? (
        <div className="voting_feed_empty">
          <svg viewBox="0 0 24 24" fill="currentColor" width="64" height="64">
            <path d="M18 13h-.68l-2 2h1.91L19 17H5l1.78-2h2.05l-2-2H6l-3 3v4c0 1.1.89 2 1.99 2H19c1.1 0 2-.89 2-2v-4l-3-3zm-1-5.05l-4.95 4.95-3.54-3.54 4.95-4.95L17 7.95zm-4.24-5.66L6.39 8.66c-.39.39-.39 1.02 0 1.41l4.95 4.95c.39.39 1.02.39 1.41 0l6.36-6.36c.39-.39.39-1.02 0-1.41L14.16 2.3c-.38-.4-1.01-.4-1.4-.01z" />
          </svg>
          <h3>Nenhuma votação encontrada</h3>
          <p>Seja o primeiro a criar uma enquete!</p>
        </div>
      ) : (
        <div className="voting_list">
          {posts.map((post) => {
            const expired = isExpired(post.votingData?.endDate);
            const voteEntry = votedPolls[post._id];
            const hasLocalVotes = Array.isArray(voteEntry) ? voteEntry.length > 0 : voteEntry !== undefined;
            const hasVoted = hasLocalVotes || post.votingData?.hasVoted;
            const totalVotes = post.votingData?.totalVotes || 0;
            const allowMultiple = !!post.votingData?.allowMultiple;
            const showResults = expired || (post.votingData?.resultsVisible !== false && hasVoted);
            const disableVoting = expired || (!allowMultiple && hasVoted);

            return (
              <div key={post._id} className={`voting_card ${expired ? "expired" : ""}`}>
                <div className="voting_card_header">
                  <Link to={`/profile/${post.user.username}`} className="voting_author">
                    <img src={post.user.picture} alt="" />
                    <div>
                      <span className="voting_author_name">{post.user.first_name} {post.user.last_name}</span>
                      <span className="voting_time"><Moment fromNow locale="pt-br">{post.createdAt}</Moment></span>
                    </div>
                  </Link>
                  {expired && <span className="voting_expired_badge">Encerrada</span>}
                  <div onClick={(e) => e.stopPropagation()}>
                    <FeedPostMenu
                      post={post}
                      onEdit={handleEditPost}
                      onPostDeleted={handlePostDeleted}
                    />
                  </div>
                </div>

                <h3>{post.votingData?.title || post.text}</h3>

                <div className="voting_options">
                  {(post.votingData?.options || []).map((option, i) => {
                    const percentage = getPercentage(option.votes || 0, totalVotes);
                    const isSelected = Array.isArray(voteEntry)
                      ? voteEntry.includes(i)
                      : voteEntry === i;

                    return (
                      <button
                        key={i}
                        className={`voting_option ${showResults ? "show_result" : ""} ${isSelected ? "selected" : ""}`}
                        onClick={() => !disableVoting && handleVote(post._id, i)}
                        disabled={disableVoting && !allowMultiple}
                      >
                        {showResults && (
                          <div className="voting_option_bar" style={{ width: `${percentage}%` }}></div>
                        )}
                        <span className="voting_option_text">{option.text}</span>
                        {showResults && <span className="voting_option_percent">{percentage}%</span>}
                      </button>
                    );
                  })}
                </div>

                <div className="voting_meta">
                  <span>{totalVotes} votos</span>
                  {post.votingData?.endDate && !expired && (
                    <>
                      <span>•</span>
                      <span>Encerra <Moment fromNow locale="pt-br">{post.votingData.endDate}</Moment></span>
                    </>
                  )}
                </div>

                <div className="voting_actions">
                  <button
                    className="voting_action_btn"
                    onClick={() => handleShare(post._id, post.votingData?.title || post.text)}
                    title="Compartilhar"
                  >
                    <span>↗️</span> Compartilhar
                  </button>
                  {showResults && (
                    <button
                      className="voting_action_btn"
                      onClick={() => handleCopyResults(post._id)}
                      title="Copiar resultados"
                    >
                      <span>📊</span> Copiar
                    </button>
                  )}
                </div>

                <VotingNotifications
                  postId={post._id}
                  isSubscribed={subscriptions[post._id] || (post.votingData?.subscribers || []).includes(user._id)}
                  onSubscriptionChange={(isSubscribed) => handleSubscriptionChange(post._id, isSubscribed)}
                />

                <VotingCreatorTools
                  postId={post._id}
                  isCreator={post.user._id === user._id}
                />
              </div>
            );
          })}
        </div>
      )}

      {hasMore && (
        <div className="voting_load_more">
          <button onClick={handleLoadMore} disabled={loading}>
            {loading ? "Carregando..." : "Ver mais votações"}
          </button>
        </div>
      )}
      {editingPost && (
        <EditPostPopup
          post={editingPost}
          user={user}
          setVisible={(visible) => !visible && setEditingPost(null)}
          onPostUpdated={handlePostUpdated}
        />
      )}
    </div>
  );
}
